import { waitDialog } from 'dialogs';
import type { CreateTarget } from 'actions';
import type { Received } from 'validation';
import { EditModel } from 'edit/EditModel';
import { getSchedulingWindow, type SchedulingWindow } from 'api/scheduling_windows/endpoints/getSchedulingWindow';
import { postSchedulingWindow } from 'api/scheduling_windows/endpoints/postSchedulingWindow';
import { putSchedulingWindow } from 'api/scheduling_windows/endpoints/putSchedulingWindow';
import { selectDefaultTimezone } from 'redux/selectors/config';
import { fetchConfigs } from 'redux/slices/configSlice';
import store from 'redux/store';

import { EditableSchedulingWindow } from './EditableSchedulingWindow';
import { EditSchedulingWindowDialog } from './EditSchedulingWindowDialog';

export type ShowEditSchedulingWindowArgs =
  { readonly mode: 'create'; readonly createIn?: CreateTarget } | { readonly mode: 'edit' | 'duplicate'; readonly id: number };

// Persist: POST when new (create or duplicate), PUT addressed by the pre-edit zone
// and name when editing an existing window.
const save = (entity: EditableSchedulingWindow): Promise<Received<SchedulingWindow>> => {
  const body = entity.toSave();
  const original = entity.original;
  return original === null ? postSchedulingWindow(body) : putSchedulingWindow(original.zoneName, original.name, body);
};

/**
 * The timezone a blank create form starts on.
 *
 * Resolved here rather than in the entity, which must not reach into the store. The
 * config may not have been fetched yet — this dialog can be the first thing a
 * session opens — so it is fetched first, and a failure falls through to whatever
 * is already in the store and then to the entity's own fallback. A create form is
 * not worth blocking over a default.
 */
const resolveDefaultTimezone = async (): Promise<string | undefined> => {
  try {
    await store.dispatch(fetchConfigs());
  } catch {
    // Leave it to the store's current contents.
  }
  return selectDefaultTimezone(store.getState()) ?? undefined;
};

/**
 * Open the scheduling-window create / edit / duplicate dialog and wait for the user
 * to save or cancel. Resolves `true` when a save succeeded (the caller should
 * refresh its list) and `undefined` when cancelled.
 *
 * Edit and duplicate always fetch the record by id (boundary-validated) rather than
 * trusting a list row, so this works from the Workspace, which never populates the
 * list slice. A missing record throws, which the dialog surfaces inline.
 */
export const showEditSchedulingWindow = async (args: ShowEditSchedulingWindowArgs): Promise<true | undefined> => {
  const duplicate = args.mode === 'duplicate';
  const createIn = args.mode === 'create' ? args.createIn : undefined;
  const defaultTimezone = args.mode === 'create' ? await resolveDefaultTimezone() : undefined;

  const initialize: () => Promise<SchedulingWindow | null> =
    args.mode === 'create'
      ? async () => null
      : async () => {
          const answer = await getSchedulingWindow(args.id);
          // Rendered by EditDialog as "Failed to load <entity>: <message>", so each
          // sentence continues that one — and each names a different useful action.
          if (answer.result === 'unreadable') throw new Error("the server's response could not be read. Please report this.");
          if (answer.result === 'degraded')
            throw new Error("part of the server's response could not be read, so saving it could overwrite that part. Please report this.");
          if (answer.value === null) throw new Error('it no longer exists; the UI may be out of date. Please refresh.');
          return answer.value;
        };

  const model = new EditModel(initialize, save, (loaded) => new EditableSchedulingWindow(loaded, { duplicate, createIn, defaultTimezone }), {
    initialMode: args.mode === 'edit' ? 'edit' : 'create',
    successMessage: args.mode === 'edit' ? 'Scheduling window updated' : 'Scheduling window created',
  });

  return waitDialog<true>((ok, cancel) => <EditSchedulingWindowDialog model={model} onSaved={() => ok(true)} onClose={cancel} />);
};
